import { useNavigate, useParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { CaretRightIcon, TrashIcon } from '@phosphor-icons/react'
import ScoopHistory from './ScoopHistory/ScoopHistory'
import styles from './DatasetInsight.module.scss'
import { ROUTES } from '../../../../router/routes'
import { Server } from '../../../../api/Server'
import { TablesInsight } from './TablesInsight/TablesInsight'
import { CalculatedColumns } from './CalculatedColumns/CalculatedColumns'
import EditCalculatedSource from './EditCalculatedSource/EditCalculatedSource'
import { DatasetCard } from './DatasetCard/DatasetCard'
import { PreviewTab } from './PreviewTab/PreviewTab'
import { useWorkspaceMetadata } from '../../../../api/hooks/workspaceMetadata/useWorkspaceMetadata'
import { useGetInboxData, useIngestionMetadata } from '../../../../api/hooks/datasource/hook'
import { DatasetSemantics } from './DatasetSemantics/DatasetSemantics'
import {
    ScoopButton,
    ScoopSpinner,
    ScoopText,
    ScoopTab,
    ScoopTabPanel,
    ScoopChip,
    ScoopRow,
    ScoopColumn,
    ScoopCenter,
    useToast,
} from '../../../ui'
import { useModal, MODALS } from '../../../modals'

export const DatasetInsight = () => {
    const { inboxID } = useParams()
    const [value, setValue] = useState(0)
    const navigate = useNavigate()
    const userID = useSelector((state) => state.auth.userID)
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const token = useSelector((state) => state.auth.token)
    const server = new Server(workspaceID, userID, token)
    const [sourceDetails, setSourceDetails] = useState({})
    const { workspaceMetadata: metadata, refetch: getMetadata } = useWorkspaceMetadata()
    const toast = useToast()
    const [hasBeenLoading, setHasBeenLoading] = useState(false)
    const [processStatus, setProcessStatus] = useState(null)

    // Modal Manager hooks
    const deleteModal = useModal(MODALS.COMMON.DELETE)
    const confirmModal = useModal(MODALS.COMMON.SAVE_CHANGES) // Using SAVE_CHANGES as generic confirm
    const [singleQuery, setSingleQuery] = useState(false)
    const inbox = useMemo(
        () => metadata?.inboxes?.find((i) => i.inboxID === inboxID),
        [inboxID, metadata?.inboxes]
    )

    const { ingestionMetadata: sourceContent, refetch, isLoading } = useIngestionMetadata(inboxID)

    // extracted here temporarily just to evade unhandled behavior
    // we need to support empty semantics in datasets, and create them instead
    const {
        data: getInboxData,
        isLoading: isLoadingGetInboxData,
        invalidateInboxData,
    } = useGetInboxData(inbox?.inboxName)

    useEffect(() => {
        checkStatus()
    }, [])

    useEffect(() => {
        if (processStatus) {
            let status = Array.from(processStatus.values())
            let isLoading = false
            status.forEach((s) => {
                if (s.curStep === -1 && inboxID === s.inboxID) isLoading = true
            })

            if (isLoading) {
                if (!hasBeenLoading) {
                    setHasBeenLoading(true)
                }
                toast.info('Processing started. Stay tuned.')
                window.setTimeout(checkStatus, 10000)
            } else if (hasBeenLoading && status.length > 0) {
                toast.success('Processing completed!')
            }
        }
    }, [processStatus])

    useEffect(() => {
        metadata?.inboxes?.find((inbox) => {
            if (inbox.inboxID === inboxID) {
                setSourceDetails(inbox)
                if (inbox.readOnly) {
                    setValue(1)
                }
            }
        })
    }, [metadata, inboxID])

    const refreshInbox = async () => {
        const metadata = await getMetadata()
        metadata?.inboxes?.find((inbox) => {
            if (inbox.inboxID === inboxID) {
                setSourceDetails(inbox)
                if (inbox.readOnly) {
                    setValue(1)
                }
            }
        })
    }
    const handleDelete = () => {
        const server = new Server(workspaceID, userID, token)
        server.postData(
            {
                action: 'delete',
                inboxID: inboxID,
                deleteAll: true,
            },
            (response) => handleAfterDelete(response),
            {},
            (response) => handleError(response)
        )
    }

    const handleConfirmDelete = async () => {
        await deleteModal.show({
            title: 'Dataset',
            description: 'Are you sure that you want to delete this dataset from Scoop?',
            onDelete: handleDelete,
        })
    }

    const handleConfirmReprocess = async () => {
        await confirmModal.show({
            title: 'Reprocess Data',
            description:
                "This may take a few minutes and you won't have access to your data in the meantime. Are you sure you want to reprocess this dataset?",
            confirmText: 'Yes, Reprocess',
            onSave: handleReprocess,
        })
    }
    const handleAfterDelete = (response) => {
        toast.success(response.message ?? 'Dataset deleted successfully')
        setTimeout(() => navigate(ROUTES.SOURCES), 5000)
    }
    const handleError = (response) => {
        toast.error(response.message ?? 'There was an error deleting the Dataset')
    }
    const checkStatus = () => {
        const server = new Server(workspaceID, userID, token)
        if (server.token) {
            server.postData(
                {
                    action: 'getProcessStatus',
                    workspaceID: workspaceID,
                },
                processStatusResult
            )
        }
    }
    const handleReprocess = () => {
        const server = new Server(workspaceID, userID, token)
        server.sheetPostData(
            {
                action: 'reProcess',
                inboxID: inboxID,
            },
            () => {
                checkStatus()
            }
        )
    }
    const processStatusResult = (result) => {
        const psMap = new Map()
        for (let i = 0; i < result.inboxStatus.length; i++) {
            psMap.set(result.inboxStatus[i].inboxID, result.inboxStatus[i])
        }
        setProcessStatus(psMap)
    }

    const handleChange = (event, newValue) => {
        setValue(newValue)
    }

    const handleBuildTabs = useMemo(() => {
        let tabs = []
        tabs.push({ key: 0, label: 'Scoop History' })
        tabs.push({
            key: 1,
            label: (
                <div className={styles.chipLabel}>
                    Tables
                    <ScoopChip size="small" className={styles.tabChip}>
                        {sourceDetails?.tables?.length}
                    </ScoopChip>
                </div>
            ),
        })
        if (sourceDetails?.tables?.length > 0 && sourceDetails?.inboxType !== 'calculated') {
            tabs.push({ key: 2, label: 'Calculated Columns' })
            tabs.push({ key: 4, label: 'Preview' })
        }
        if (sourceDetails?.inboxType === 'calculated' && sourceDetails?.calculatedWorksheetID) {
            tabs.push({ key: 3, label: 'Modify Calculated Source' })
            tabs.push({ key: 4, label: 'Preview' })
        }
        if (inbox?.tables.length > 0) {
            tabs.push({
                key: 5,
                label: 'Dataset Semantics',
            })
        }
        return tabs
    }, [sourceContent, sourceDetails, getInboxData])

    return (
        <>
            <div className={styles.datasetInsightsContainer}>
                <div className={styles.pageWrapper}>
                    <div className={styles.datasetInsightsTopnav}>
                        <ScoopRow align="center" className={styles.topNavActions}>
                            <ScoopText
                                className={styles.breadcrumbTextGray}
                                onClick={() => navigate(ROUTES.SOURCES)}
                            >
                                Datasets
                            </ScoopText>
                            <CaretRightIcon size={16} />
                            <ScoopText className={styles.breadcrumbTextGray}>
                                {sourceContent?.inboxLabel}
                            </ScoopText>
                        </ScoopRow>
                        <div className={styles.actionsRow}>
                            {!sourceDetails.readOnly && (
                                <ScoopButton onClick={handleConfirmReprocess}>
                                    Reprocess Data
                                </ScoopButton>
                            )}
                            {!sourceDetails.readOnly && (
                                <ScoopButton
                                    variant="text"
                                    size="xxs"
                                    onClick={handleConfirmDelete}
                                >
                                    <TrashIcon size={20} className={styles.deleteIcon} />
                                </ScoopButton>
                            )}
                        </div>
                    </div>
                </div>
                <div className={styles.datasetCardContainer}>
                    <DatasetCard
                        server={server}
                        dataset={sourceDetails}
                        refreshInbox={refreshInbox}
                    />
                </div>
                <div className={styles.tabsMainContainer}>
                    <div className={styles.datasetTabsContainer}>
                        <ScoopColumn className={styles.tabsScrollBox}>
                            <div className={styles.tabsInnerBox}>
                                <ScoopTab
                                    tabs={handleBuildTabs}
                                    value={value}
                                    onChange={(e, value) => handleChange(e, value)}
                                />
                            </div>
                            <ScoopColumn className={styles.tabsContentBox}>
                                {isLoading && (
                                    <ScoopCenter className={styles.loaderBox}>
                                        <ScoopSpinner />
                                    </ScoopCenter>
                                )}
                                <ScoopTabPanel
                                    value={0}
                                    selectedValue={value}
                                    className={styles.tabPanelContent}
                                >
                                    {sourceContent?.ingestions && (
                                        <ScoopHistory
                                            ingestionLogs={sourceContent?.ingestions}
                                            tableName={sourceContent?.inboxLabel}
                                            inboxID={inboxID}
                                            refreshLogs={refetch}
                                            tables={sourceDetails?.tables}
                                            onReprocess={handleConfirmReprocess}
                                            dataset={sourceDetails}
                                        />
                                    )}
                                </ScoopTabPanel>
                                <ScoopTabPanel
                                    value={1}
                                    selectedValue={value}
                                    className={styles.tabPanelContent}
                                >
                                    {sourceDetails?.tables && (
                                        <TablesInsight
                                            getMetadata={getMetadata}
                                            inboxID={inboxID}
                                            readOnly={sourceDetails?.readOnly}
                                            tables={sourceDetails?.tables}
                                            datasetName={sourceDetails?.label}
                                        ></TablesInsight>
                                    )}
                                </ScoopTabPanel>
                                {sourceDetails?.tables && (
                                    <ScoopTabPanel
                                        value={2}
                                        selectedValue={value}
                                        className={styles.tabPanelContent}
                                    >
                                        <CalculatedColumns
                                            inboxID={inboxID}
                                            sourceDetails={sourceDetails}
                                            setConfirm
                                        />
                                    </ScoopTabPanel>
                                )}
                                {
                                    <ScoopTabPanel
                                        value={3}
                                        selectedValue={value}
                                        className={styles.tabPanelContent}
                                    >
                                        <EditCalculatedSource
                                            inboxID={inboxID}
                                            singleQuery={singleQuery}
                                            setSingleQuery={setSingleQuery}
                                        />
                                    </ScoopTabPanel>
                                }
                                {
                                    <ScoopTabPanel
                                        value={4}
                                        selectedValue={value}
                                        className={styles.tabPanelContent}
                                    >
                                        <PreviewTab
                                            value={value}
                                            inboxID={inboxID}
                                            server={server}
                                            singleQuery={singleQuery}
                                        />
                                    </ScoopTabPanel>
                                }
                                {inbox?.tables.length > 0 && (
                                    <ScoopTabPanel
                                        value={5}
                                        selectedValue={value}
                                        className={styles.tabPanelContent}
                                    >
                                        <DatasetSemantics
                                            isLoadingGetInboxData={isLoadingGetInboxData}
                                            getInboxData={getInboxData}
                                            invalidateInboxData={invalidateInboxData}
                                            inbox={inbox}
                                        />
                                    </ScoopTabPanel>
                                )}
                            </ScoopColumn>
                        </ScoopColumn>
                    </div>
                </div>
            </div>
        </>
    )
}
